import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../connector/api';

export default function GradingSchemaModal({ schema, onClose, onSuccess }) {
    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [brackets, setBrackets] = useState([]);

    useEffect(() => {
        if (schema) {
            setName(schema.name || '');
            setIsDefault(schema.is_default || false);
            setBrackets(schema.brackets || []);
        } else {
            setName('');
            setIsDefault(false);
            // Default sample bracket set
            setBrackets([
                { letter_grade: 'A', min_score: '90.00', max_score: '100.00', color_code: '#10B981', description: 'Excellent' },
                { letter_grade: 'B', min_score: '80.00', max_score: '89.99', color_code: '#3B82F6', description: 'Very Good' },
                { letter_grade: 'C', min_score: '70.00', max_score: '79.99', color_code: '#F59E0B', description: 'Satisfactory' },
                { letter_grade: 'F', min_score: '0.00', max_score: '69.99', color_code: '#EF4444', description: 'Failing' }
            ]);
        }
    }, [schema]);

    const handleBracketChange = (index, field, value) => {
        setBrackets(prev => {
            const updated = [...prev];
            updated[index][field] = value;
            return updated;
        });
    };

    const handleAddBracket = () => {
        setBrackets(prev => [
            ...prev,
            { letter_grade: '', min_score: '0.00', max_score: '0.00', color_code: '#3B82F6', description: '' }
        ]);
    };

    const handleRemoveBracket = (index) => {
        setBrackets(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Schema name is required.");
            return;
        }

        setIsSaving(true);
        const payload = {
            name,
            is_default: isDefault,
            brackets
        };

        try {
            if (schema?.id) {
                await api.put(`grading-schemas/${schema.id}/`, payload);
                toast.success("Grading schema updated successfully!");
            } else {
                await api.post('grading-schemas/', payload);
                toast.success("Grading schema created successfully!");
            }
            onSuccess();
        } catch (err) {
            console.error("Failed to save grading schema:", err);
            toast.error("Failed to save grading schema.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-xs p-4 overflow-y-auto">
            <div className="bg-white border border-zinc-200 rounded-lg shadow-xl w-full max-w-3xl overflow-hidden my-8">
                
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-900">
                            {schema ? "Edit Grading Schema" : "New Grading Schema"}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-0.5">
                            Define the schema name and grade thresholds.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-zinc-400 hover:text-zinc-600 text-lg font-semibold leading-none"
                    >
                        &times;
                    </button>
                </div>

                {/* Modal Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    
                    {/* Primary Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                        <div className="sm:col-span-2 space-y-1">
                            <label className="text-xs font-semibold text-zinc-700">Schema Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g., Standard Letter Grade Scale"
                                className="w-full px-3 py-2 text-xs border border-zinc-300 rounded-lg focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>
                        <div className="flex items-center pb-2.5">
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-zinc-700">
                                <input
                                    type="checkbox"
                                    checked={isDefault}
                                    onChange={(e) => setIsDefault(e.target.checked)}
                                    className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                />
                                Set as Default Scale
                            </label>
                        </div>
                    </div>

                    {/* Nested Grade Brackets List */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                            <h4 className="text-xs font-bold text-zinc-900">Grade Brackets</h4>
                            <button
                                type="button"
                                onClick={handleAddBracket}
                                className="px-2.5 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors"
                            >
                                + Add Bracket
                            </button>
                        </div>

                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1 custom-scroll">
                            {brackets.map((bracket, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-12 gap-2 items-center bg-zinc-50 p-2.5 rounded-lg border border-zinc-200"
                                >
                                    {/* Grade */}
                                    <div className="col-span-2">
                                        <label className="text-[10px] text-zinc-500 block font-medium">Grade</label>
                                        <input
                                            type="text"
                                            value={bracket.letter_grade}
                                            onChange={(e) => handleBracketChange(index, 'letter_grade', e.target.value)}
                                            placeholder="A"
                                            className="w-full px-2 py-1 text-xs border border-zinc-300 rounded focus:outline-none focus:border-blue-500"
                                            required
                                        />
                                    </div>

                                    {/* Min Score */}
                                    <div className="col-span-2">
                                        <label className="text-[10px] text-zinc-500 block font-medium">Min Score</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={bracket.min_score}
                                            onChange={(e) => handleBracketChange(index, 'min_score', e.target.value)}
                                            className="w-full px-2 py-1 text-xs border border-zinc-300 rounded focus:outline-none focus:border-blue-500"
                                            required
                                        />
                                    </div>

                                    {/* Max Score */}
                                    <div className="col-span-2">
                                        <label className="text-[10px] text-zinc-500 block font-medium">Max Score</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={bracket.max_score}
                                            onChange={(e) => handleBracketChange(index, 'max_score', e.target.value)}
                                            className="w-full px-2 py-1 text-xs border border-zinc-300 rounded focus:outline-none focus:border-blue-500"
                                            required
                                        />
                                    </div>

                                    {/* Color */}
                                    <div className="col-span-2">
                                        <label className="text-[10px] text-zinc-500 block font-medium">Color</label>
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="color"
                                                value={bracket.color_code || '#3B82F6'}
                                                onChange={(e) => handleBracketChange(index, 'color_code', e.target.value)}
                                                className="w-7 h-7 p-0.5 border border-zinc-300 rounded cursor-pointer bg-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="col-span-3">
                                        <label className="text-[10px] text-zinc-500 block font-medium">Description</label>
                                        <input
                                            type="text"
                                            value={bracket.description || ''}
                                            onChange={(e) => handleBracketChange(index, 'description', e.target.value)}
                                            placeholder="Excellent"
                                            className="w-full px-2 py-1 text-xs border border-zinc-300 rounded focus:outline-none focus:border-blue-500"
                                        />
                                    </div>

                                    {/* Actions */}
                                    <div className="col-span-1 text-right pt-3">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveBracket(index)}
                                            className="text-rose-500 hover:text-rose-700 text-xs font-bold"
                                            title="Remove bracket"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                            {isSaving ? "Saving..." : "Save Grading Schema"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}